import React, { useEffect, useMemo, useState } from 'react'
import { lockBodyScroll } from '@/utils/bodyScrollLock'
import { MdClose, MdChevronLeft, MdChevronRight } from 'react-icons/md'
import type { DeliveryDate } from '@/utils/deliverySchedule'
import { toStartOfDay } from '@/utils/deliverySchedule'
import { toLocalDateString } from '@/utils/deliveryDays'
import { formatDate } from '@/utils/dateFormat'
import { movedDeliveryTargets } from '@/utils/deliveryOverlay'
import type { DeliveryDateOverride, RoundDayAmendment } from '@/types'
import '@/styles/components/holiday-calendar-modal.css'

const WEEKDAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

/** How many months of calendar to show at once. */
const MONTHS_SHOWN = 3

/** A month index (year * 12 + month), so paging is plain arithmetic. */
const monthIndexOf = (d: Date): number => d.getFullYear() * 12 + d.getMonth()
const monthFromIndex = (idx: number): Date => new Date(Math.floor(idx / 12), idx % 12, 1)

/** A month as Monday-first weeks, padded with nulls so every row has 7 cells. */
const buildMonth = (year: number, month: number): (Date | null)[][] => {
  const first = new Date(year, month, 1)
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  const cells: (Date | null)[] = []
  const leadingBlanks = (first.getDay() + 6) % 7
  for (let i = 0; i < leadingBlanks; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d))
  while (cells.length % 7 !== 0) cells.push(null)

  const weeks: (Date | null)[][] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
  return weeks
}

export interface HolidayDelta {
  date: Date
  /** true = skip this delivery, false = un-skip it. */
  skip: boolean
}

interface Props {
  isOpen: boolean
  onClose: () => void
  /** The customer's projected deliveries; only these can be toggled. */
  deliveries: DeliveryDate[]
  /** The staged changes, applied together when the customer saves. */
  onSave: (deltas: HolidayDelta[]) => void
  saving?: boolean
  /** Start the view on this month, e.g. an existing holiday's. */
  initialMonthDate?: Date | null
  /** Render inline as a full page rather than as an overlay (mobile route). */
  asPage?: boolean
  /** Earliest day a pause can start (the holiday 5pm cut-off, YYYY-MM-DD).
   *  Days before it show their colour but can't be tapped. */
  minPauseDate?: string | null
  /** Deliveries moved to another day (DF-345) and the dates customers are
   *  shown (DF-473). A moved delivery is still paused on its own day (DF-473
   *  rule 2), but its pause closes earlier, from the day it actually comes. */
  roundAmendments?: RoundDayAmendment[]
  dateOverrides?: DeliveryDateOverride[]
}

/**
 * Book holidays on a calendar that reads like the My Deliveries one: a blue box
 * is a delivery, a red box is a holiday. Clicking a delivery flips it between
 * the two, so there is nothing to learn beyond the colours.
 *
 * Three months are shown at once, with arrows to page further ahead. Only the
 * customer's own future deliveries are clickable; one past its cutoff shows its
 * colour but is locked. Every toggle is staged locally and nothing is sent
 * until Save, so the whole holiday goes to the server as one set.
 */
const HolidayCalendarModal: React.FC<Props> = ({
  isOpen,
  onClose,
  deliveries,
  onSave,
  saving = false,
  initialMonthDate = null,
  asPage = false,
  minPauseDate = null,
  roundAmendments = [],
  dateOverrides = [],
}) => {
  /** Where each moved delivery actually arrives, keyed by its own date. */
  const movedTo = useMemo(
    () => movedDeliveryTargets(roundAmendments, dateOverrides),
    [roundAmendments, dateOverrides]
  )
  /** Moved deliveries whose (earlier) pause cut-off has passed. */
  const pauseClosed = useMemo(
    () => new Set(roundAmendments.filter(a => a.pause_closed).map(a => a.date_from)),
    [roundAmendments]
  )
  const today = useMemo(() => toStartOfDay(new Date()), [])
  const currentIndex = monthIndexOf(today)

  // The earliest day a pause can start. Holidays have their own 5pm cut-off, so
  // this is usually a day or two ahead of today; days before it can't be paused.
  const minPause = useMemo(
    () => (minPauseDate ? toStartOfDay(new Date(minPauseDate)) : today),
    [minPauseDate, today]
  )

  /** The last month any delivery falls in, so paging can't run off the end. */
  const lastDeliveryIndex = useMemo(() => {
    let max = currentIndex
    deliveries.forEach(d => { max = Math.max(max, monthIndexOf(d.date)) })
    return max
  }, [deliveries, currentIndex])

  // One month at a time on the page (mobile), where a pinned bottom bar carries
  // the pager and Save; three across in the desktop modal.
  const monthsShown = asPage ? 1 : MONTHS_SHOWN
  const maxStartIndex = Math.max(currentIndex, lastDeliveryIndex - (monthsShown - 1))

  /** The first month on screen, as a month index. Paged by the arrows. */
  const [startIndex, setStartIndex] = useState(currentIndex)

  /** Explicit skip toggles, keyed by day; absent means "leave as booked". */
  const [skips, setSkips] = useState<Record<string, boolean>>({})

  // Reset toggles and jump to the wanted month each time the dialog opens.
  useEffect(() => {
    if (!isOpen) return
    setSkips({})
    const wanted = initialMonthDate ? monthIndexOf(initialMonthDate) : currentIndex
    setStartIndex(Math.max(currentIndex, Math.min(maxStartIndex, wanted)))
  }, [isOpen])

  useEffect(() => {
    // As a page there is no overlay to trap focus in, and locking body scroll
    // would freeze the page itself. The Escape-to-close is a modal affordance
    // too, so skip the whole effect.
    if (asPage) return

    const onEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    let releaseScroll: (() => void) | undefined
    if (isOpen) {
      document.addEventListener('keydown', onEscape)
      releaseScroll = lockBodyScroll()
    }
    return () => {
      document.removeEventListener('keydown', onEscape)
      releaseScroll?.()
    }
  }, [isOpen, onClose, asPage])

  /** Deliveries keyed by day, so a calendar cell can find its own. */
  const byDate = useMemo(() => {
    const map = new Map<string, DeliveryDate>()
    deliveries.forEach(d => map.set(toLocalDateString(d.date), d))
    return map
  }, [deliveries])

  /** A delivery that can still be paused: editable and on or after the pause
   *  cut-off. Days before it are shown locked rather than tappable, so a pause
   *  that the server would reject can't be staged in the first place. */
  const isChangeable = (d: DeliveryDate): boolean =>
    d.isEditable && d.date >= minPause && !pauseClosed.has(toLocalDateString(d.date))

  /** Whether a delivery is staged as skipped: the toggle, else what's booked. */
  const stagedSkip = (d: DeliveryDate): boolean =>
    skips[toLocalDateString(d.date)] ?? d.isHoliday

  const deltas = useMemo<HolidayDelta[]>(() => {
    const out: HolidayDelta[] = []
    deliveries.forEach(d => {
      if (!isChangeable(d)) return
      const staged = stagedSkip(d)
      if (staged !== d.isHoliday) out.push({ date: d.date, skip: staged })
    })
    return out
  }, [skips, deliveries])

  const skippedCount = useMemo(
    () => deliveries.filter(d => d.date >= today && stagedSkip(d)).length,
    [skips, deliveries, today]
  )

  if (!isOpen) return null

  const todayKey = toLocalDateString(today)
  const months = Array.from({ length: monthsShown }, (_, i) => monthFromIndex(startIndex + i))
  const canPrev = startIndex > currentIndex
  const canNext = startIndex < maxStartIndex

  const prevMonthBtn = (
    <button
      type="button"
      className="hcal-month-nav"
      onClick={() => setStartIndex(i => Math.max(currentIndex, i - 1))}
      disabled={!canPrev}
      aria-label="Earlier month"
    >
      <MdChevronLeft />
    </button>
  )
  const nextMonthBtn = (
    <button
      type="button"
      className="hcal-month-nav"
      onClick={() => setStartIndex(i => Math.min(maxStartIndex, i + 1))}
      disabled={!canNext}
      aria-label="Later month"
    >
      <MdChevronRight />
    </button>
  )
  // Modal: both arrows grouped in the header. Page: they flank the month label
  // (see the calendars below), so only the combined form is used up here.
  const monthNav = <div className="hcal-nav">{prevMonthBtn}{nextMonthBtn}</div>

  const toggle = (key: string) => setSkips(prev => ({ ...prev, [key]: !(prev[key] ?? byDate.get(key)?.isHoliday ?? false) }))

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  return (
    <div
      className={asPage ? 'hcal-page' : 'modal-backdrop'}
      onClick={asPage ? undefined : handleBackdropClick}
    >
      <div
        className={`hcal-modal ${asPage ? 'hcal-page-inner' : 'modal-container'}`}
        role={asPage ? undefined : 'dialog'}
        aria-modal={asPage ? undefined : true}
        aria-label="Pause deliveries"
      >
        {!asPage && (
          <button
            type="button"
            className="modal-close-floating"
            onClick={onClose}
            aria-label="Close"
          >
            <MdClose aria-hidden="true" />
          </button>
        )}

        <div className="modal-content">
          <h2 className="hcal-title">Pause deliveries</h2>
          <p className="hcal-intro">
            Tap any delivery to pause it. Blue is a delivery, red is paused.
          </p>

          <div className="hcal-head">
            <div className="hcal-legend" aria-hidden="true">
              <span className="hcal-legend-item"><span className="hcal-swatch is-delivery" /> Delivering</span>
              <span className="hcal-legend-item"><span className="hcal-swatch is-skipped" /> Paused</span>
              <span className="hcal-legend-item"><span className="hcal-swatch is-too-soon" /> Too close to pause</span>
              {movedTo.size > 0 && (
                <span className="hcal-legend-item"><span className="hcal-swatch is-moved" /> Delivered on another day</span>
              )}
            </div>

            {!asPage && monthNav}
          </div>

          <div className="hcal-calendars">
            {months.map(month => (
              <div key={`${month.getFullYear()}-${month.getMonth()}`} className="hcal-calendar">
                <p className={`hcal-month-label${asPage ? ' hcal-month-label-nav' : ''}`}>
                  {asPage && prevMonthBtn}
                  <span>{month.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
                  {asPage && nextMonthBtn}
                </p>

                <div className="sun-calendar-grid">
                  {WEEKDAY_INITIALS.map((initial, j) => (
                    <span key={`h-${j}`} className="sun-calendar-weekday" aria-hidden="true">{initial}</span>
                  ))}

                  {buildMonth(month.getFullYear(), month.getMonth()).flat().map((date, j) => {
                    if (!date) return <span key={`b-${j}`} className="sun-calendar-blank" />

                    const key = toLocalDateString(date)
                    const delivery = byDate.get(key)
                    const isToday = key === todayKey

                    // DF-473 rule 2: a delivery moved to another day is still
                    // paused here, on its own day, so it stays tappable. It's
                    // marked so the customer knows when it actually comes.
                    const arrivesOn = delivery ? movedTo.get(key) : undefined
                    const movedClass = arrivesOn ? ' is-rescheduled' : ''
                    const movedNote = arrivesOn ? `Delivered on ${formatDate(arrivesOn)} instead. ` : ''

                    // A non-delivery day, or a delivery already in the past:
                    // just the number, nothing to change.
                    if (!delivery || date < today) {
                      return (
                        <span
                          key={key}
                          className={`sun-calendar-day${isToday ? ' is-today' : ''}`}
                        >
                          {date.getDate()}
                        </span>
                      )
                    }

                    const skipped = stagedSkip(delivery)
                    const stateClass = (skipped ? ' is-paused' : ' is-delivery') + movedClass

                    // A day with no delivery and no pause on it is nothing to
                    // pause, so it reads as a plain day rather than a tappable
                    // one (FEDEV-615). A day that is already paused stays
                    // tappable so it can be un-paused.
                    if (delivery.items.length === 0 && ! skipped) {
                      return (
                        <span
                          key={key}
                          className={`sun-calendar-day${isToday ? ' is-today' : ''}`}
                        >
                          {date.getDate()}
                        </span>
                      )
                    }

                    // Not changeable: either too close to today to pause yet
                    // (before the holiday cut-off, amber) or past its own cutoff
                    // (locked, dimmed). Both show their colour but can't be tapped.
                    if (!isChangeable(delivery)) {
                      const tooSoon = delivery.date < minPause
                      return (
                        <span
                          key={key}
                          className={`sun-calendar-day ${tooSoon ? 'is-too-soon' : 'is-locked'}${stateClass}${isToday ? ' is-today' : ''}`}
                          title={movedNote + (tooSoon
                            ? 'This delivery is too close to today to pause.'
                            : 'This delivery can no longer be changed.')}
                        >
                          {date.getDate()}
                        </span>
                      )
                    }

                    return (
                      <button
                        key={key}
                        type="button"
                        className={`sun-calendar-day${stateClass}${isToday ? ' is-today' : ''}`}
                        onClick={() => toggle(key)}
                        aria-pressed={skipped}
                        title={movedNote || undefined}
                        aria-label={`${date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}, ${movedNote}${skipped ? 'paused, tap to deliver' : 'delivering, tap to pause'}`}
                      >
                        {date.getDate()}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className={`hcal-foot${asPage ? ' hcal-foot-page' : ''}`}>
            <span className="hcal-summary">
              {skippedCount === 0
                ? 'No deliveries paused'
                : `${skippedCount} ${skippedCount === 1 ? 'delivery' : 'deliveries'} paused`}
            </span>
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={() => onSave(deltas)}
              disabled={saving || deltas.length === 0}
            >
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HolidayCalendarModal
